import React, { useCallback, useState, type ReactNode } from "react";

import AuditRestoreConfirmationForm from "../../components/forms/audit/AuditRestoreConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import type {
  AuditApiEntityType,
  AuditEvent,
  AuditPaginatedResponse,
} from "../../components/audit/auditTypes";
import { generateUniqueNumber } from "../../shared/utility";
import { makePostApiCall } from "../../shared/makeApiCall";
import { restoreFromAudit, type AuditRestoreResponse } from "./auditApi";

type AddSnackbarFn = (snackbar: SnackbarData) => void;

export type AuditHistoryViewModelState = {
  audits: AuditEvent[];
  emptyModal: {
    open: boolean;
    childComponent: ReactNode;
  };
  getAudits: () => void;
  hasMore: boolean;
  loadMore: () => void;
  loading: boolean;
  loadingMore: boolean;
  restoringAuditId: number | string | null;
  requestRestore: (event: AuditEvent) => void;
  setEmptyModelOpen: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      childComponent: ReactNode;
    }>
  >;
};

export function useAuditHistoryViewModel(
  logout: () => void,
  entityId: number | string | null | undefined,
  entityType: AuditApiEntityType | null | undefined,
  addSnackbar?: AddSnackbarFn,
): AuditHistoryViewModelState {
  const [audits, setAudits] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [restoringAuditId, setRestoringAuditId] = useState<
    number | string | null
  >(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [emptyModal, setEmptyModelOpen] = useState<{
    open: boolean;
    childComponent: ReactNode;
  }>({
    open: false,
    childComponent: null,
  });

  const showErrorModal = useCallback(
    (titleOrSubtitle: string, subtitleMaybe?: string) => {
      const title = subtitleMaybe !== undefined ? titleOrSubtitle : "Error";
      const subtitle =
        subtitleMaybe !== undefined ? subtitleMaybe : titleOrSubtitle;
      setEmptyModelOpen({
        childComponent: (
          <ModelErrorComponent
            setOpen={setEmptyModelOpen}
            subtitle={subtitle}
            title={title}
          />
        ),
        open: true,
      });
    },
    [],
  );

  const showToast = useCallback(
    (message: string, type: string = SnackbarType.Success) => {
      if (!addSnackbar) return;
      addSnackbar(new SnackbarData(generateUniqueNumber(), message, type));
    },
    [addSnackbar],
  );

  const fetchPage = useCallback(
    async (page: number, append = false) => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      const callResult = entityId
        ? await makePostApiCall("fetchAuditsForEntity", {
            entity_id: entityId,
            entity_type: entityType,
            page,
          })
        : await makePostApiCall("fetchAuditsForEntityType", {
            entity_type: entityType,
            page,
          });

      if (page === 1) setLoading(false);
      else setLoadingMore(false);

      if ("success" in callResult && callResult.success) {
        const payload = callResult.data as AuditPaginatedResponse;
        const newAudits = payload.data ?? [];
        setAudits(append ? (prev) => [...prev, ...newAudits] : newAudits);
        setLastPage(payload.last_page ?? 1);
        setCurrentPage(page);
      } else if (
        "errorStatus" in callResult &&
        callResult.errorStatus === 401
      ) {
        logout();
      } else {
        const title =
          page === 1
            ? "Could not load audit history"
            : "Could not load more audit history";
        const errorData =
          "data" in callResult
            ? (callResult.data as { message?: string } | undefined)
            : undefined;
        const message = errorData?.message ?? "Please try again.";
        showErrorModal(title, message);
      }
    },
    [entityId, entityType, logout, showErrorModal],
  );

  const getAudits = useCallback(() => fetchPage(1, false), [fetchPage]);

  const loadMore = useCallback(() => {
    if (currentPage < lastPage && !loadingMore) {
      fetchPage(currentPage + 1, true);
    }
  }, [currentPage, lastPage, loadingMore, fetchPage]);

  const confirmRestore = useCallback(
    async (auditId: number | string) => {
      setEmptyModelOpen({ open: false, childComponent: null });
      setRestoringAuditId(auditId);

      const callResult = await restoreFromAudit(auditId);

      setRestoringAuditId(null);

      if ("success" in callResult && callResult.success) {
        setEmptyModelOpen({ open: false, childComponent: null });
        const data = callResult.data as AuditRestoreResponse;
        showToast(data.message ?? "Record restored successfully.");
        if (data.warnings?.length) {
          showToast(data.warnings.join(" "), SnackbarType.Error);
        }
        getAudits();
        return;
      }

      if ("errorStatus" in callResult && callResult.errorStatus === 401) {
        setEmptyModelOpen({ open: false, childComponent: null });
        logout();
        return;
      }

      const errorData =
        "data" in callResult
          ? (callResult.data as { message?: string } | undefined)
          : undefined;
      const errorStatus =
        "errorStatus" in callResult ? callResult.errorStatus : undefined;
      const message =
        errorData?.message ?? "Could not restore this audit record.";
      // Supervisor-only gate disabled temporarily.
      // (errorStatus === 403
      //   ? "Only supervisors can restore audit records."
      //   : "Could not restore this audit record.");

      if (errorStatus === 403) {
        setEmptyModelOpen({ open: false, childComponent: null });
        showToast(message, SnackbarType.Error);
        return;
      }

      setEmptyModelOpen({ open: false, childComponent: null });
      showErrorModal("Could not restore", message);
    },
    [getAudits, logout, showErrorModal, showToast],
  );

  const requestRestore = useCallback(
    (event: AuditEvent) => {
      const action = event.metadata?.action;
      const isUpdated = action === "updated";

      setEmptyModelOpen({
        open: true,
        childComponent: (
          <AuditRestoreConfirmationForm
            title={
              isUpdated
                ? "Revert changes from this update?"
                : "Restore this deleted record?"
            }
            message={
              isUpdated
                ? "This will roll back to their previous values."
                : "This will bring back the deleted record."
            }
            confirmLabel={isUpdated ? "Revert" : "Restore"}
            confirmAction={() => confirmRestore(event.id)}
            setOpen={setEmptyModelOpen}
          />
        ),
      });
    },
    [confirmRestore],
  );

  return {
    audits,
    emptyModal,
    getAudits,
    hasMore: currentPage < lastPage,
    loadMore,
    loading,
    loadingMore,
    restoringAuditId,
    requestRestore,
    setEmptyModelOpen,
  };
}
